/**
 * Member coach header-context change path (DNKADM-0301; DNKAPI-0460).
 * The member-detail read seeds the value; the standalone coach read is used
 * only to refresh the server-truthful value after an assign or clear mutation.
 */
import { useRef, useState } from 'react';
import { ApiError } from '@/api/errors';
import { clearMemberCoach, getMemberCoach, setMemberCoach, type MemberCoach } from '@/api/members';
import { v2ErrorCode } from '@/api/v2Errors';
import { useToast } from '@/components/useToast';
import { toStaffAssignmentErrorMessage } from './membersError';

export type CoachAssignmentState = 'unassigned' | 'assigned' | 'orphaned';

export function coachAssignmentState(coach: MemberCoach): CoachAssignmentState {
  if (coach.staffId === null) return 'unassigned';
  return coach.staffName === null ? 'orphaned' : 'assigned';
}

export function coachDisplayName(coach: MemberCoach): string {
  switch (coachAssignmentState(coach)) {
    case 'assigned':
      return coach.staffName ?? '';
    case 'orphaned':
      return 'Assigned staffer no longer available';
    case 'unassigned':
      return 'Coach: not set';
  }
}

export function useCoachChange({
  contactId,
  onChanged,
}: {
  contactId: string;
  onChanged: (coach: MemberCoach) => void;
}) {
  const showToast = useToast();
  const [open, setOpenState] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const [pickerVersion, setPickerVersion] = useState(0);
  const retryAction = useRef<(() => void) | null>(null);

  function setOpen(next: boolean) {
    if (next) {
      setError(null);
      setCanRetry(false);
      retryAction.current = null;
    }
    setOpenState(next);
  }

  function showFailure(err: unknown, retry: (() => void) | null) {
    const missingStaffer = v2ErrorCode(err) === 'MEMBER_STAFF_NOT_FOUND';
    const missingMember = err instanceof ApiError && (err.status === 403 || err.status === 404);
    setError(toStaffAssignmentErrorMessage(err));
    if (missingStaffer) setPickerVersion((value) => value + 1);
    retryAction.current = missingStaffer || missingMember ? null : retry;
    setCanRetry(retryAction.current !== null);
  }

  async function refreshAfterMutation() {
    setBusy(true);
    setError(null);
    setCanRetry(false);
    try {
      const refreshed = await getMemberCoach(contactId);
      onChanged(refreshed);
      retryAction.current = null;
      setOpenState(false);
    } catch (err) {
      const missingMember = err instanceof ApiError && (err.status === 403 || err.status === 404);
      setError(
        missingMember
          ? toStaffAssignmentErrorMessage(err)
          : 'Coach updated, but the refreshed assignment could not be loaded. Please retry.',
      );
      retryAction.current = missingMember ? null : () => void refreshAfterMutation();
      setCanRetry(retryAction.current !== null);
    } finally {
      setBusy(false);
    }
  }

  async function assign(staffId: string) {
    setBusy(true);
    setError(null);
    setCanRetry(false);
    try {
      const result = await setMemberCoach(contactId, staffId);
      onChanged(result);
      const shownName = coachDisplayName(result);
      showToast(`Coach assigned: ${shownName}.`);
      await refreshAfterMutation();
    } catch (err) {
      showFailure(err, () => void assign(staffId));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setError(null);
    setCanRetry(false);
    try {
      const result = await clearMemberCoach(contactId);
      onChanged(result);
      showToast('Coach removed.');
      await refreshAfterMutation();
    } catch (err) {
      showFailure(err, () => void clear());
    } finally {
      setBusy(false);
    }
  }

  function retry() {
    retryAction.current?.();
  }

  return {
    open,
    setOpen,
    busy,
    error,
    canRetry,
    pickerVersion,
    assign,
    clear,
    retry,
  };
}
